import { supabase } from '@/integrations/supabase/client';

export interface PlayerStat {
  stat_id: number;
  player_id: string;
  game_id?: number;
  sport_id?: number;
  stat_type: string;
  stat_value: number;
  stat_submitted_by: string;
  submitter_role: 'player' | 'coach' | 'team-owner' | 'venue' | 'scorekeeper';
  is_confirmed: boolean;
  confirmed_by?: string;
  confirmed_at?: string;
  season_year?: number;
  created_at: string;
  updated_at: string;
}

export interface PlayerTotalStats {
  id: string;
  player_id: string;
  sport_id?: number;
  season_year?: number;
  total_games_played: number;
  total_points: number;
  total_assists: number;
  total_goals: number;
  total_rebounds: number;
  total_saves: number;
  total_shots: number;
  total_fouls: number;
  updated_at: string;
}

export interface PlayerGoal {
  id: string;
  player_id: string;
  goal_type: 'physical_fitness' | 'goals_per_game' | 'assists_per_game' | 'custom';
  goal_title: string;
  target_value: number;
  current_value: number;
  season_year?: number;
  target_date?: string;
  status: 'active' | 'completed' | 'paused' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export interface PlayerSelfReflection {
  id: string;
  player_id: string;
  game_id?: number;
  expected_performance: any;
  actual_performance: any;
  reflection_notes?: string;
  areas_to_improve: string[];
  created_at: string;
  updated_at: string;
}

export const playerStatsService = {
  // Submit stats for a player
  async submitPlayerStats(
    playerId: string,
    gameId: number | undefined,
    sportId: number | undefined,
    statType: string,
    statValue: number,
    submitterId: string,
    submitterRole: 'player' | 'coach' | 'team-owner' | 'venue' | 'scorekeeper',
    seasonYear?: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('player_stats')
        .insert({
          player_id: playerId,
          game_id: gameId,
          sport_id: sportId,
          stat_type: statType,
          stat_value: statValue,
          stat_submitted_by: submitterId,
          submitter_role: submitterRole,
          season_year: seasonYear || new Date().getFullYear(),
          is_confirmed: submitterRole !== 'player' // Auto-confirm for non-player submissions
        })
        .select()
        .single();

      if (error) {
        console.error('Error submitting player stats:', error);
        return { success: false, error: error.message };
      }

      console.log('Player stats submitted successfully:', data);
      return { success: true };
    } catch (error) {
      console.error('Error submitting player stats:', error);
      return { success: false, error: 'Failed to submit stats' };
    }
  },

  // Get stats for a specific player
  async getPlayerStats(playerId: string, seasonYear?: number): Promise<PlayerStat[]> {
    try {
      let query = supabase
        .from('player_stats')
        .select('*')
        .eq('player_id', playerId);

      if (seasonYear) {
        query = query.eq('season_year', seasonYear);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching player stats:', error);
        return [];
      }

      return (data || []) as PlayerStat[];
    } catch (error) {
      console.error('Error fetching player stats:', error);
      return [];
    }
  },

  // Get unconfirmed stats for a specific game or player
  async getUnconfirmedStats(gameId?: number, playerId?: string): Promise<PlayerStat[]> {
    try {
      let query = supabase
        .from('player_stats')
        .select('*')
        .eq('is_confirmed', false);

      if (gameId) {
        query = query.eq('game_id', gameId);
      }
      if (playerId) {
        query = query.eq('player_id', playerId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching unconfirmed stats:', error);
        return [];
      }

      return (data || []) as PlayerStat[];
    } catch (error) {
      console.error('Error fetching unconfirmed stats:', error);
      return [];
    }
  },

  // Get detailed approval info for a stat
  async getStatApprovalInfo(statId: number): Promise<any> {
    try {
      const { data, error } = await supabase.rpc('get_stat_approval_info', { p_stat_id: statId });
      
      if (error) {
        console.error('Error getting stat approval info:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error getting stat approval info:', error);
      return null;
    }
  },

  // Approve a stat (dual approval system)
  async approveStat(statId: number, approverId: string, approverRole: 'player' | 'coach' | 'team-owner'): Promise<{ success: boolean; error?: string; confirmed?: boolean; message?: string }> {
    try {
      const { data, error } = await supabase.rpc('process_stat_approval', {
        p_stat_id: statId,
        p_approver_id: approverId,
        p_approver_role: approverRole
      });

      if (error) {
        console.error('Error approving stat:', error);
        return { success: false, error: error.message };
      }

      const result = data as any;
      if (result?.success === false) {
        return { success: false, error: result.error };
      }

      return { 
        success: true, 
        confirmed: result?.confirmed || false,
        message: result?.message || 'Approval processed'
      };
    } catch (error) {
      console.error('Error approving stat:', error);
      return { success: false, error: 'Failed to approve stat' };
    }
  },

  // Legacy confirm stat method (kept for backwards compatibility)
  async confirmStat(statId: number, confirmedBy: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('player_stats')
        .update({
          is_confirmed: true,
          confirmed_by: confirmedBy,
          confirmed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('stat_id', statId)
        .select()
        .single();

      if (error) {
        console.error('Error confirming stat:', error);
        return { success: false, error: error.message };
      }

      console.log('Stat confirmed successfully:', data);
      return { success: true };
    } catch (error) {
      console.error('Error confirming stat:', error);
      return { success: false, error: 'Failed to confirm stat' };
    }
  },

  // Get player's total stats
  async getPlayerTotalStats(playerId: string, seasonYear?: number): Promise<PlayerTotalStats[]> {
    try {
      let query = supabase
        .from('player_total_stats')
        .select('*')
        .eq('player_id', playerId);

      if (seasonYear) {
        query = query.eq('season_year', seasonYear);
      }

      const { data, error } = await query.order('season_year', { ascending: false });

      if (error) {
        console.error('Error fetching player total stats:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching player total stats:', error);
      return [];
    }
  },

  // Player Goals Management
  async createPlayerGoal(
    playerId: string,
    goalType: 'physical_fitness' | 'goals_per_game' | 'assists_per_game' | 'custom',
    goalTitle: string,
    targetValue: number,
    seasonYear?: number,
    targetDate?: string
  ): Promise<PlayerGoal> {
    const { data, error } = await supabase
      .from('player_goals')
      .insert({
        player_id: playerId,
        goal_type: goalType,
        goal_title: goalTitle,
        target_value: targetValue,
        season_year: seasonYear || new Date().getFullYear(),
        target_date: targetDate
      })
      .select()
      .single();

    if (error) throw error;
    return data as PlayerGoal;
  },

  async getPlayerGoals(playerId: string, seasonYear?: number): Promise<PlayerGoal[]> {
    let query = supabase
      .from('player_goals')
      .select('*')
      .eq('player_id', playerId);

    if (seasonYear) {
      query = query.eq('season_year', seasonYear);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as PlayerGoal[];
  },

  async updatePlayerGoal(goalId: string, currentValue: number): Promise<void> {
    const { error } = await supabase
      .from('player_goals')
      .update({
        current_value: currentValue,
        updated_at: new Date().toISOString()
      })
      .eq('id', goalId);

    if (error) throw error;
  },

  // Self-reflection management
  async createSelfReflection(
    playerId: string,
    gameId: number | undefined,
    expectedPerformance: any,
    actualPerformance: any,
    reflectionNotes?: string,
    areasToImprove: string[] = []
  ): Promise<PlayerSelfReflection> {
    const { data, error } = await supabase
      .from('player_self_reflection')
      .insert({
        player_id: playerId,
        game_id: gameId,
        expected_performance: expectedPerformance,
        actual_performance: actualPerformance,
        reflection_notes: reflectionNotes,
        areas_to_improve: areasToImprove
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getPlayerReflections(playerId: string): Promise<PlayerSelfReflection[]> {
    const { data, error } = await supabase
      .from('player_self_reflection')
      .select('*')
      .eq('player_id', playerId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Calculate goal progress
  async calculateGoalProgress(playerId: string, goalType: string, seasonYear?: number): Promise<number> {
    const year = seasonYear || new Date().getFullYear();
    
    // Get current stats for the goal type
    const stats = await this.getPlayerStats(playerId, year);
    
    // Calculate based on goal type
    switch (goalType) {
      case 'goals_per_game':
        const totalGoals = stats.filter(s => s.stat_type === 'goals').reduce((sum, s) => sum + s.stat_value, 0);
        const totalGames = stats.filter(s => s.stat_type === 'games_played').reduce((sum, s) => sum + s.stat_value, 0);
        return totalGames > 0 ? totalGoals / totalGames : 0;
      
      case 'assists_per_game':
        const totalAssists = stats.filter(s => s.stat_type === 'assists').reduce((sum, s) => sum + s.stat_value, 0);
        const gamesPlayed = stats.filter(s => s.stat_type === 'games_played').reduce((sum, s) => sum + s.stat_value, 0);
        return gamesPlayed > 0 ? totalAssists / gamesPlayed : 0;
      
      default:
        return 0;
    }
  },

  // Get performance comparison metrics
  async getPerformanceMetrics(playerId: string, seasonYear?: number): Promise<any> {
    const year = seasonYear || new Date().getFullYear();
    const stats = await this.getPlayerStats(playerId, year);
    const goals = await this.getPlayerGoals(playerId, year);
    
    // Calculate goal achievement rates
    const goalMetrics = await Promise.all(
      goals.map(async (goal) => {
        const currentProgress = await this.calculateGoalProgress(playerId, goal.goal_type, year);
        const achievementRate = goal.target_value > 0 ? (currentProgress / goal.target_value) * 100 : 0;
        
        return {
          ...goal,
          current_progress: currentProgress,
          achievement_rate: achievementRate,
          status: achievementRate >= 100 ? 'met' : 'missed'
        };
      })
    );

    const metGoals = goalMetrics.filter(g => g.status === 'met').length;
    const totalGoals = goalMetrics.length;
    const goalSuccessRate = totalGoals > 0 ? (metGoals / totalGoals) * 100 : 0;

    return {
      goal_metrics: goalMetrics,
      goal_success_rate: goalSuccessRate,
      total_stats: stats.length,
      confirmed_stats: stats.filter(s => s.is_confirmed).length
    };
  },

  // Get all stats for admin management
  async getAllStats(): Promise<PlayerStat[]> {
    try {
      const { data, error } = await supabase
        .from('player_stats')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching all stats:', error);
        return [];
      }

      return (data || []) as PlayerStat[];
    } catch (error) {
      console.error('Error fetching all stats:', error);
      return [];
    }
  }
};